import { navigate } from '@reach/router';
import React from 'react';
import styled from 'styled-components/macro';
import { useApiRequest } from '../../../api/useApiRequest';
import { IfUserHasPermission } from '../../../authorisation/IfUserHasPermission';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { urls } from '../../../helpers/urls';
import { ReactComponent as AddIcon } from '../../../images/icons/add.svg';
import { BreadcrumbBar, BreadcrumbLink, BreadcrumbSeparator } from '../../../layout/Breadcrumbs';
import { ButtonIconLeft, PrimaryButton } from '../../../shared/Button';
import { GetRequest } from '../../../shared/GetRequest';
import { Header1 } from '../../../shared/Headers';
import { colourNeutral02 } from '../../../styling/colours';
import { spacing03, spacing05, spacing06, spacing07 } from '../../../styling/spacing';
import { ProjectDesigns } from '../../designs/project-designs/ProjectDesigns';
import { ProjectQuotes } from '../../quotes/project-quotes/ProjectQuotes';
import { Project, ProjectHeader } from '../project-section/ProjectHeader';
import { ProjectQuotesAndDesignsResponse } from '../projectQuotesAndDesignsResponse';
import { projectUrls } from '../projectUrls';

type Props = { project: Project } & RouteComponentProps;

export const ProjectDetails = ({ project }: Props) => {
  const fetchQuotesAndDesignsRequest = useApiRequest<ProjectQuotesAndDesignsResponse>({
    httpMethod: 'get',
    endpointUrl: `projects/${project.projectId}/quotes-and-designs`,
  });

  return (
    <>
      <BreadcrumbBar>
        <BreadcrumbLink to={urls.home}>Projects</BreadcrumbLink>
        <BreadcrumbSeparator />
        <div>{project.projectReference}</div>
      </BreadcrumbBar>
      <ProjectHeader project={project} />
      <GetRequest getDataRequest={fetchQuotesAndDesignsRequest}>
        {({ response: { customerQuotes, designs }, sendRequest }) => (
          <>
            <Section>
              <HeaderRow>
                <Header1>Quotes</Header1>
                <IfUserHasPermission requiredPermission="Edit">
                  <PrimaryButton onClick={() => navigate(projectUrls.newQuote(project.projectId))}>
                    <ButtonIconLeft>
                      <AddIcon />
                    </ButtonIconLeft>
                    New Quote
                  </PrimaryButton>
                </IfUserHasPermission>
              </HeaderRow>
              <ProjectQuotes projectId={project.projectId} customerQuotes={customerQuotes} />
            </Section>
            <Section>
              <HeaderRow>
                <Header1>Designs</Header1>
              </HeaderRow>
              <ProjectDesigns projectId={project.projectId} designs={designs} refreshDesigns={sendRequest} />
            </Section>
          </>
        )}
      </GetRequest>
    </>
  );
};

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing05};

  ${Header1} {
    color: ${colourNeutral02};
    margin: 0;
  }
`;

const Section = styled.div`
  padding: ${spacing06} ${spacing07} ${spacing03};
`;
